// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
// Intervall-Timer: läuft zeitstempel-basiert (korrekt nach Handy-Sperre),
// letzte 3 Sekunden jeder Phase mit großem Countdown + Piep + Vibration.
import { useEffect, useMemo, useRef, useState } from "react"
import type { TimerConfig } from "@/lib/types"
import { buildPhases, stateAt, unlockAudio, playBeep, vibrate, requestWakeLock, type IntervalState } from "@/lib/timer"
import { IconLautsprecher } from "@/components/icons"
import "@/styles/timer.css"

interface Props {
  config: TimerConfig
  onDone?: () => void
  onClose?: () => void
}

const SOUND_KEY = "fithub:timer-sound"

export default function IntervalTimer({ config, onDone, onClose }: Props) {
  const phases = useMemo(() => buildPhases(config), [config])
  const [startedAt, setStartedAt] = useState<number | null>(null)
  const [pausedElapsed, setPausedElapsed] = useState(0)
  const [state, setState] = useState<IntervalState>(() => stateAt(phases, 0))
  const [sound, setSound] = useState(() => localStorage.getItem(SOUND_KEY) !== "aus")
  const lastCue = useRef<string>("")
  const wakeLock = useRef<{ release: () => void } | null>(null)

  const running = startedAt !== null && !state.done

  useEffect(() => {
    if (startedAt === null) return
    const id = setInterval(() => {
      const elapsed = pausedElapsed + (Date.now() - startedAt) / 1000
      const st = stateAt(phases, elapsed)
      setState(st)

      // Signale: einmal pro (Phase, Restsekunde) — Ref verhindert Doppel-Piepen
      const cueKey = st.done ? "done" : `${st.phaseIndex}:${st.remainingSec}`
      if (cueKey !== lastCue.current) {
        lastCue.current = cueKey
        if (st.done) {
          if (sound) { playBeep(320, 990); setTimeout(() => playBeep(320, 990), 380) }
          vibrate([140, 80, 140])
        } else if (st.countdown) {
          if (sound) playBeep(110, st.phase?.kind === "work" ? 660 : 880)
          vibrate(50)
        } else if (st.remainingSec === st.phase?.durationSec) {
          // Phasenwechsel (volle Restzeit einer neuen Phase)
          if (sound) playBeep(240, st.phase.kind === "work" ? 880 : 590)
          vibrate(90)
        }
      }

      if (st.done) {
        clearInterval(id)
        wakeLock.current?.release()
        onDone?.()
      }
    }, 250)
    return () => clearInterval(id)
  }, [startedAt, pausedElapsed, phases, sound, onDone])

  useEffect(() => () => wakeLock.current?.release(), [])

  async function start() {
    unlockAudio()
    wakeLock.current = await requestWakeLock()
    lastCue.current = ""
    setStartedAt(Date.now())
  }

  function pause() {
    if (startedAt === null) return
    setPausedElapsed(pausedElapsed + (Date.now() - startedAt) / 1000)
    setStartedAt(null)
    wakeLock.current?.release()
  }

  function reset() {
    setStartedAt(null)
    setPausedElapsed(0)
    setState(stateAt(phases, 0))
    lastCue.current = ""
    wakeLock.current?.release()
  }

  function toggleSound() {
    const next = !sound
    setSound(next)
    localStorage.setItem(SOUND_KEY, next ? "an" : "aus")
  }

  const phase = state.phase
  const kindClass = state.done ? "done" : phase?.kind === "rest" ? "rest" : "work"

  return (
    <div className={`itimer ${kindClass}${state.countdown ? " countdown" : ""}`} role="timer">
      <div className="itimer-head">
        <span className="itimer-label">
          {state.done
            ? "Geschafft!"
            : phase?.kind === "rest"
              ? `Pause · vor Runde ${Math.min(phase.round + 1, phase.totalRounds)}/${phase.totalRounds}`
              : `Arbeit · Runde ${phase?.round ?? 1}/${phase?.totalRounds ?? config.rounds}`}
        </span>
        <button type="button" className="itimer-sound" onClick={toggleSound} aria-label={sound ? "Ton ausschalten" : "Ton einschalten"}>
          <IconLautsprecher size={15} aus={!sound} /> {sound ? "Ton aus" : "Ton an"}
        </button>
      </div>
      <div className="itimer-time num-hero" aria-live={state.countdown ? "assertive" : "off"}>
        {state.done ? "✓" : state.remainingSec}
      </div>
      <div className="itimer-actions">
        {state.done ? (
          <>
            <button type="button" className="btn-secondary" onClick={reset}>Nochmal starten</button>
            {onClose && <button type="button" className="btn-secondary" onClick={onClose}>Schließen</button>}
          </>
        ) : running ? (
          <button type="button" className="btn-secondary" onClick={pause}>Pausieren</button>
        ) : (
          <>
            <button type="button" className="save itimer-start" onClick={() => void start()}>
              {pausedElapsed > 0 ? "Fortsetzen" : "Timer starten"}
            </button>
            {pausedElapsed > 0 && <button type="button" className="btn-secondary" onClick={reset}>Zurücksetzen</button>}
            {onClose && pausedElapsed === 0 && <button type="button" className="btn-secondary" onClick={onClose}>Schließen</button>}
          </>
        )}
      </div>
    </div>
  )
}
